/**
 * ================================================
 * NOTIFICATION SETTINGS - Configuration Système
 * ================================================
 * Gère les préférences utilisateur pour les notifications
 * Permet de réduire la surcharge de pop-ups
 */

export const DEFAULT_NOTIFICATION_SETTINGS = {
  // Types de notifications à afficher
  showTicketCalled: true,        // Afficher "Ticket appelé"
  showQueueUpdated: false,       // Masquer "File mise à jour" (trop fréquent)
  showNewTicket: false,          // Masquer "Nouveau ticket" (trop fréquent)
  showTicketFinished: false,     // Masquer "Ticket terminé"
  showTicketTransferred: true,   // Afficher "Ticket transféré"
  showTicketAutoFinished: true,  // Afficher "Ticket terminé automatiquement"
  showQueueEmpty: true,          // Afficher "File vide"
  showDeviceAlert: true,         // Afficher alertes appareils

  // Paramètres audio
  playSound: true,               // Jouer un son
  soundVolume: 0.5,              // Volume (0-1)

  // Groupement des notifications
  groupSimilar: true,            // Grouper notifications similaires
  groupDelay: 3000,              // Délai en ms pour grouper (3s)

  // Durée d'affichage
  autoClose: true,               // Fermer automatiquement
  autoCloseDelay: 5000           // Délai avant fermeture auto (5s)
};

/**
 * Récupère les paramètres de notification de l'utilisateur
 * @returns {Object} Paramètres avec les valeurs par défaut si nécessaire
 */
export function getNotificationSettings() {
  try {
    const saved = localStorage.getItem("notificationSettings");
    if (saved) {
      const parsed = JSON.parse(saved);
      // Fusionner avec les valeurs par défaut pour les nouvelles clés
      return { ...DEFAULT_NOTIFICATION_SETTINGS, ...parsed };
    }
  } catch (err) {
    console.error("❌ Erreur chargement settings notifications:", err);
  }
  return { ...DEFAULT_NOTIFICATION_SETTINGS };
}

/**
 * Sauvegarde les paramètres de notification
 * @param {Object} settings - Nouveaux paramètres
 */
export function saveNotificationSettings(settings) {
  try {
    localStorage.setItem("notificationSettings", JSON.stringify(settings));
    return true;
  } catch (err) {
    console.error("❌ Erreur sauvegarde settings notifications:", err);
    return false;
  }
}

/**
 * Réinitialise les paramètres aux valeurs par défaut
 */
export function resetNotificationSettings() {
  return saveNotificationSettings(DEFAULT_NOTIFICATION_SETTINGS);
}

/**
 * Vérifie si une notification doit être affichée selon les paramètres
 * @param {string} notificationType - Type de notification
 * @returns {boolean} true si doit être affiché
 */
export function shouldShowNotification(notificationType) {
  const settings = getNotificationSettings();

  const typeMap = {
    "ticket_called": settings.showTicketCalled,
    "queue_updated": settings.showQueueUpdated,
    "new_ticket_waiting": settings.showNewTicket,
    "ticket_finished": settings.showTicketFinished,
    "ticket_transferred": settings.showTicketTransferred,
    "ticket_auto_finished": settings.showTicketAutoFinished,
    "queue_empty": settings.showQueueEmpty,
    "device_alert": settings.showDeviceAlert
  };

  return typeMap[notificationType] !== false; // Par défaut: afficher
}

/**
 * Helper pour envoyer une notification via le NotificationCenter
 * Vérifie automatiquement les paramètres avant d'afficher
 * @param {string} type - Type de notification (ex: "ticket_called")
 * @param {Object} data - { icon, title, message, notifType (success/error/warning/info) }
 */
export function sendNotification(type, data) {
  if (!shouldShowNotification(type)) {
    return; // Ne pas afficher
  }

  const settings = getNotificationSettings();

  // Vérifier si le NotificationCenter est disponible
  if (window.$notificationCenter) {
    window.$notificationCenter.addNotification({
      icon: data.icon || "🔔",
      title: data.title,
      message: data.message,
      type: data.notifType || "info",
      playSound: settings.playSound
    });
  } else {
    console.warn("⚠️ NotificationCenter non disponible");
  }
}

export default {
  DEFAULT_NOTIFICATION_SETTINGS,
  getNotificationSettings,
  saveNotificationSettings,
  resetNotificationSettings,
  shouldShowNotification,
  sendNotification
};
